import type { PurchasesPlugin } from '@revenuecat/purchases-capacitor';
import {
  creditStoreProductId,
  PLAN_STORE_PRODUCT,
  planAtLeast,
  type BillingInterval,
  type CreditPackId,
  type PaidPlan,
} from '@secretario/shared';
import { isNativeShell } from './platform.js';
import { readSession } from './session.js';

/**
 * Assinatura pela loja do aparelho (Google Play e App Store), via RevenueCat.
 * O app só mostra compra quando a chave da loja existe no build; sem ela, o
 * app instalado não tem botão nem link de pagamento (regra das lojas).
 * Quem confirma o plano é o servidor, pelo aviso que a loja manda a ele.
 */
type Platform = 'android' | 'ios' | 'web';

function platform(): Platform {
  const capacitor = (window as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const name = capacitor?.getPlatform?.();
  return name === 'android' || name === 'ios' ? name : 'web';
}

/** Loja deste aparelho, no nome que o servidor usa para o canal de venda. */
export function storeChannel(): 'google_play' | 'app_store' | null {
  const kind = platform();
  if (kind === 'android') return 'google_play';
  if (kind === 'ios') return 'app_store';
  return null;
}

function storeKey(): string | undefined {
  const env = import.meta.env as Record<string, string | undefined>;
  const kind = platform();
  if (kind === 'android') return env.VITE_REVENUECAT_ANDROID_KEY || undefined;
  if (kind === 'ios') return env.VITE_REVENUECAT_IOS_KEY || undefined;
  return undefined;
}

/** A compra pela loja está disponível neste aparelho? */
export function storeReady(): boolean {
  return isNativeShell() && !!storeKey();
}

/** Nome da loja, para os textos de assinatura. */
export function storeName(): string {
  return platform() === 'ios' ? 'App Store' : 'Google Play';
}

let configured: Promise<PurchasesPlugin> | null = null;
/** Conta ligada à loja na última chamada; muda quando outra pessoa entra no aparelho. */
let configuredFor: string | null = null;

async function purchases() {
  const userId = readSession()?.user.id;
  if (!userId) throw new Error('A compra pela loja não está disponível agora.');
  configured ??= (async () => {
    const key = storeKey();
    if (!key) throw new Error('A compra pela loja não está disponível agora.');
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    // O id da conta é o mesmo do servidor: o aviso da loja chega com ele.
    await Purchases.configure({ apiKey: key, appUserID: userId });
    configuredFor = userId;
    return Purchases;
  })().catch((error: unknown) => {
    configured = null;
    configuredFor = null;
    throw error;
  });
  const Purchases = await configured;
  // Outra conta entrou depois de a loja já estar ligada: a loja passa a ver a conta nova.
  if (configuredFor !== userId) {
    await Purchases.logIn({ appUserID: userId });
    configuredFor = userId;
  }
  return Purchases;
}

/** Ao sair da conta, a loja esquece quem estava ligado (a próxima entrada liga de novo). */
export async function storeLogOut(): Promise<void> {
  if (!configured) return;
  try {
    const Purchases = await configured;
    await Purchases.logOut();
  } catch {
    /* sem loja ligada ou já anônimo: nada a fazer */
  } finally {
    configuredFor = null;
  }
}

/**
 * Preços de cada plano como a loja mostra ("R$ 39,90"), no mensal e no anual,
 * e se a loja oferece teste grátis nele. Vazio quando a loja não tem o plano.
 */
export type StorePrices = {
  monthly: Partial<Record<PaidPlan, string>>;
  annual: Partial<Record<PaidPlan, string>>;
  /** Mensalidade equivalente do anual ("R$ 33,25"). */
  annualPerMonth: Partial<Record<PaidPlan, string>>;
  trial: Partial<Record<PaidPlan, boolean>>;
};

type StorePackage = {
  product: {
    priceString: string;
    pricePerMonthString: string | null;
    introPrice: { price: number } | null;
    defaultOption: { freePhase: unknown } | null;
  };
};

/** A loja dá teste grátis neste produto? (Play: fase grátis; App Store: preço de entrada zero) */
function hasFreeTrial(item: StorePackage | null | undefined): boolean {
  const product = item?.product;
  if (!product) return false;
  return Boolean(product.defaultOption?.freePhase) || product.introPrice?.price === 0;
}

/** Preços dos planos direto da loja: no app instalado, é a loja quem diz o valor. */
export async function storePrices(): Promise<StorePrices> {
  const Purchases = await purchases();
  const { all } = await Purchases.getOfferings();
  const prices: StorePrices = { monthly: {}, annual: {}, annualPerMonth: {}, trial: {} };
  for (const plan of ['basic', 'pro'] as const) {
    const monthly = all[plan]?.monthly ?? all[plan]?.availablePackages[0];
    if (monthly?.product.priceString) prices.monthly[plan] = monthly.product.priceString;
    const annual = all[plan]?.annual;
    if (annual?.product.priceString) {
      prices.annual[plan] = annual.product.priceString;
      if (annual.product.pricePerMonthString) {
        prices.annualPerMonth[plan] = annual.product.pricePerMonthString;
      }
    }
    if (hasFreeTrial(monthly as StorePackage | undefined)) prices.trial[plan] = true;
  }
  return prices;
}

/** Produto da loja sem o sufixo do plano base ("secretario_pessoal:mensal" vira "secretario_pessoal"). */
function baseProductId(productId: string): string {
  return productId.split(':')[0] ?? productId;
}

/** Assinatura pela loja que a pessoa já tem, para a troca de plano virar uma troca e não uma segunda compra. */
export type StoreSubscription = { plan: PaidPlan; productId: string | null };

/**
 * Abre a compra do plano na loja. true quando a loja concluiu a compra.
 * Com uma assinatura da loja em vigor, a compra vira troca de plano: subir
 * cobra a diferença na hora; descer vale na próxima renovação (Google Play).
 */
export async function buyInStore(
  plan: PaidPlan,
  current: StoreSubscription | null = null,
  interval: BillingInterval = 'month',
): Promise<boolean> {
  const Purchases = await purchases();
  const offerings = await Purchases.getOfferings();
  const offering = offerings.all[plan];
  const item =
    interval === 'year' ? offering?.annual : (offering?.monthly ?? offering?.availablePackages[0]);
  if (!item) throw new Error('Esse plano ainda não está disponível na loja.');
  let googleProductChangeInfo = null;
  const sameProduct = current?.plan === plan;
  if (
    current &&
    (!sameProduct || current.productId !== item.product.identifier) &&
    platform() === 'android'
  ) {
    const { PRORATION_MODE } = await import('@revenuecat/purchases-capacitor');
    const upgrade = planAtLeast(plan, current.plan);
    googleProductChangeInfo = {
      oldProductIdentifier: baseProductId(current.productId ?? PLAN_STORE_PRODUCT[current.plan]),
      prorationMode: upgrade
        ? PRORATION_MODE.IMMEDIATE_AND_CHARGE_PRORATED_PRICE
        : PRORATION_MODE.DEFERRED,
    };
  }
  try {
    await Purchases.purchasePackage({ aPackage: item, googleProductChangeInfo });
    return true;
  } catch (error) {
    // Fechar a tela da loja não é erro.
    if ((error as { userCancelled?: boolean }).userCancelled) return false;
    throw error;
  }
}

/**
 * Compra de um pacote de créditos na loja (produto consumível). Os pacotes
 * ficam na oferta "creditos" do RevenueCat, um pacote por produto.
 */
export async function buyCreditsInStore(pack: CreditPackId): Promise<boolean> {
  const Purchases = await purchases();
  const offerings = await Purchases.getOfferings();
  const productId = creditStoreProductId(pack);
  const item = offerings.all.creditos?.availablePackages.find(
    (entry) => entry.product.identifier === productId,
  );
  if (!item) throw new Error('Os créditos ainda não estão disponíveis na loja.');
  try {
    await Purchases.purchasePackage({ aPackage: item });
    return true;
  } catch (error) {
    if ((error as { userCancelled?: boolean }).userCancelled) return false;
    throw error;
  }
}

/** "Restaurar compras": traz de volta a assinatura feita com a mesma conta da loja. */
export async function restoreStorePurchases(): Promise<void> {
  const Purchases = await purchases();
  await Purchases.restorePurchases();
}

/** Página da loja para gerenciar ou cancelar a assinatura. */
export async function storeManagementUrl(): Promise<string> {
  const Purchases = await purchases();
  const { customerInfo } = await Purchases.getCustomerInfo();
  return (
    customerInfo.managementURL ??
    (platform() === 'ios'
      ? 'https://apps.apple.com/account/subscriptions'
      : 'https://play.google.com/store/account/subscriptions')
  );
}
